import * as React from "react";

import {
    Button,
    Divider,
    Grid,
    Typography,
    Card,
    Tabs,
    Tab,
} from "@mui/material";

import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { useNavigate, useParams } from "react-router";

import { resizeImageAuto } from "../../utils/resizeImgUtil";
import { useTablon } from "../../contexts/contextGeneral";
import imagenotfound from "../../image/Image-not-found.png";

const API_URL = "https://apiclimas.abdielaldana.com/";

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
});

function ImagenesOrden() {
    const { id } = useParams();
    const navigate = useNavigate();

    const {
        getOrden,
        postImagenOrden,
        putImagenOrden,
        postComparativaOrden,
        putComparativaOrden,
    } = useTablon();

    const [tab, setTab] = React.useState(0);
    const [orden, setOrden] = React.useState(null);

    const [imagenes, setImagenes] = React.useState([]);
    const [comparativas, setComparativas] = React.useState([]);

    const [imagenEditId, setImagenEditId] = React.useState(null);
    const [descripcionImagen, setDescripcionImagen] = React.useState("");
    const [fotoImagen, setFotoImagen] = React.useState(null);
    const [previewImagen, setPreviewImagen] = React.useState(null);

    const [comparativaEditId, setComparativaEditId] = React.useState(null);
    const [descripcionAntes, setDescripcionAntes] = React.useState("");
    const [descripcionDespues, setDescripcionDespues] = React.useState("");
    const [fotoAntes, setFotoAntes] = React.useState(null);
    const [fotoDespues, setFotoDespues] = React.useState(null);
    const [previewAntes, setPreviewAntes] = React.useState(null);
    const [previewDespues, setPreviewDespues] = React.useState(null);

    const [guardandoImagen, setGuardandoImagen] = React.useState(false);
    const [guardandoComparativa, setGuardandoComparativa] = React.useState(false);

    React.useEffect(() => {
        cargarOrden();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const cargarOrden = async () => {
        const res = await getOrden(id);
        const data = res?.data ?? res;

        if (!data) return;

        setOrden(data);
        setImagenes(data.imagenes ?? []);
        setComparativas(data.comparativas ?? []);
    };

    const returnOrders = () => {
        navigate("/ordenes");
    };

    const getSrc = (ruta) => {
        if (!ruta) return imagenotfound;
        if (String(ruta).startsWith("http")) return ruta;
        return API_URL + ruta;
    };

    const fileToPreview = (file, callback) => {
        const reader = new FileReader();

        reader.onloadend = () => {
            callback(reader.result);
        };

        reader.readAsDataURL(file);
    };

    const handleImagenFile = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const optimized = await resizeImageAuto(file);

        setFotoImagen(optimized);
        fileToPreview(optimized, setPreviewImagen);
    };

    const handleAntesFile = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const optimized = await resizeImageAuto(file);

        setFotoAntes(optimized);
        fileToPreview(optimized, setPreviewAntes);
    };

    const handleDespuesFile = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const optimized = await resizeImageAuto(file);

        setFotoDespues(optimized);
        fileToPreview(optimized, setPreviewDespues);
    };

    const limpiarFormImagen = () => {
        setImagenEditId(null);
        setDescripcionImagen("");
        setFotoImagen(null);
        setPreviewImagen(null);
    };

    const limpiarFormComparativa = () => {
        setComparativaEditId(null);
        setDescripcionAntes("");
        setDescripcionDespues("");
        setFotoAntes(null);
        setFotoDespues(null);
        setPreviewAntes(null);
        setPreviewDespues(null);
    };

    const editarImagen = (item) => {
        setImagenEditId(item.id);
        setDescripcionImagen(item.descripcion ?? "");
        setFotoImagen(null);
        setPreviewImagen(getSrc(item.ruta));
        setTab(0);
    };

    const editarComparativa = (item) => {
        setComparativaEditId(item.id);
        setDescripcionAntes(item.descripcion_antes ?? "");
        setDescripcionDespues(item.descripcion_despues ?? "");
        setFotoAntes(null);
        setFotoDespues(null);
        setPreviewAntes(getSrc(item.ruta_antes));
        setPreviewDespues(getSrc(item.ruta_despues));
        setTab(1);
    };

    const guardarImagen = async () => {
        if (!imagenEditId && !fotoImagen) return;

        setGuardandoImagen(true);

        const formData = new FormData();

        if (imagenEditId) {
            formData.append("id", imagenEditId);
        } else {
            formData.append("orden_id", id);
        }

        formData.append("descripcion", descripcionImagen);

        if (fotoImagen) {
            formData.append("foto", fotoImagen);
        }

        const res = imagenEditId
            ? await putImagenOrden(formData)
            : await postImagenOrden(formData);

        if (res?.ok) {
            limpiarFormImagen();
            await cargarOrden();
        }

        setGuardandoImagen(false);
    };

    const guardarComparativa = async () => {
        if (!comparativaEditId && (!fotoAntes || !fotoDespues)) return;

        setGuardandoComparativa(true);

        const formData = new FormData();

        if (comparativaEditId) {
            formData.append("id", comparativaEditId);
        } else {
            formData.append("orden_id", id);
        }

        formData.append("descripcion_antes", descripcionAntes);
        formData.append("descripcion_despues", descripcionDespues);

        if (fotoAntes) {
            formData.append("foto_antes", fotoAntes);
        }

        if (fotoDespues) {
            formData.append("foto_despues", fotoDespues);
        }

        const res = comparativaEditId
            ? await putComparativaOrden(formData)
            : await postComparativaOrden(formData);

        if (res?.ok) {
            limpiarFormComparativa();
            await cargarOrden();
        }

        setGuardandoComparativa(false);
    };

    return (
        <>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 12 }}>
                    <Typography
                        variant="body1"
                        color="warning"
                        onClick={returnOrders}
                        sx={{ cursor: "pointer" }}
                    >
                        Regresar a Ordenes
                    </Typography>

                    <center>
                        <Typography variant="h6" color="primary">
                            Evidencias de la Orden
                        </Typography>

                        <Typography variant="h4" color="primary">
                            <b>Orden {orden?.ot ?? ""}</b>
                        </Typography>
                    </center>
                </Grid>

                <Grid size={{ xs: 12, md: 12 }}>
                    <Divider />
                </Grid>
            </Grid>

            <Card sx={{ padding: "10px", margin: "10px 0" }}>
                <Tabs
                    value={tab}
                    onChange={(e, value) => setTab(value)}
                    variant="fullWidth"
                >
                    <Tab label="Evidencias" />
                    <Tab label="Registro" />
                </Tabs>
            </Card>

            {tab === 0 && (
                <>
                    <Card sx={{ padding: "20px 10px", margin: "10px 0" }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 12 }}>
                                <center>
                                    <Typography variant="h5" color="primary">
                                        <b>{imagenEditId ? "Editar evidencia" : "Nueva evidencia"}</b>
                                    </Typography>
                                </center>
                            </Grid>

                            <Grid size={{ xs: 12, md: 4 }} className="centerH">
                                <div className="imagenVehiculo">
                                    <img
                                        src={previewImagen || imagenotfound}
                                        alt="Evidencia"
                                        width="100%"
                                    />
                                </div>
                            </Grid>

                            <Grid size={{ xs: 12, md: 8 }}>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 12 }}>
                                        <TextField
                                            label="Descripción"
                                            fullWidth
                                            multiline
                                            rows={4}
                                            value={descripcionImagen}
                                            onChange={(e) => setDescripcionImagen(e.target.value)}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 12 }}>
                                        <Button
                                            component="label"
                                            tabIndex={-1}
                                            size="small"
                                            color={fotoImagen ? "secondary" : "primary"}
                                            variant="contained"
                                            startIcon={<CameraAltIcon />}
                                            fullWidth
                                        >
                                            {fotoImagen ? "Imagen cargada" : "Agregar imagen"}
                                            <VisuallyHiddenInput
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImagenFile}
                                            />
                                        </Button>
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 12 }}>
                                        <Button
                                            variant="contained"
                                            color="success"
                                            fullWidth
                                            onClick={guardarImagen}
                                            disabled={guardandoImagen}
                                        >
                                            {imagenEditId ? "Guardar cambios" : "Guardar evidencia"}
                                        </Button>
                                    </Grid>

                                    {imagenEditId && (
                                        <Grid size={{ xs: 12, md: 12 }}>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                fullWidth
                                                onClick={limpiarFormImagen}
                                            >
                                                Cancelar edición
                                            </Button>
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Card>

                    <Card sx={{ padding: "20px 10px", margin: "10px 0" }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 12 }}>
                                <center>
                                    <Typography variant="h5" color="primary">
                                        <b>Evidencias guardadas</b>
                                    </Typography>
                                </center>
                            </Grid>

                            {imagenes.length === 0 && (
                                <Grid size={{ xs: 12, md: 12 }}>
                                    <Typography color="text.secondary">
                                        No hay evidencias registradas.
                                    </Typography>
                                </Grid>
                            )}

                            {imagenes.map((item) => (
                                <Grid size={{ xs: 12, md: 6 }} key={item.id}>
                                    <Card sx={{ padding: "15px" }}>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, md: 12 }}>
                                                <img
                                                    src={getSrc(item.ruta)}
                                                    alt={item.descripcion}
                                                    width="100%"
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 12 }}>
                                                <Typography variant="body1">
                                                    {item.descripcion || "Sin descripción"}
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 12 }}>
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    fullWidth
                                                    onClick={() => editarImagen(item)}
                                                >
                                                    Editar evidencia
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Card>
                </>
            )}

            {tab === 1 && (
                <>
                    <Card sx={{ padding: "20px 10px", margin: "10px 0" }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 12 }}>
                                <center>
                                    <Typography variant="h5" color="primary">
                                        <b>{comparativaEditId ? "Editar registro" : "Nuevo registro"}</b>
                                    </Typography>
                                </center>
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <Card sx={{ padding: "10px" }}>
                                    <Typography variant="h6" color="primary">
                                        <b>Antes</b>
                                    </Typography>

                                    <img
                                        src={previewAntes || imagenotfound}
                                        alt="Antes"
                                        width="100%"
                                    />

                                    <TextField
                                        label="Descripción antes"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        sx={{ marginTop: "10px" }}
                                        value={descripcionAntes}
                                        onChange={(e) => setDescripcionAntes(e.target.value)}
                                    />

                                    <Button
                                        component="label"
                                        tabIndex={-1}
                                        size="small"
                                        color={fotoAntes ? "secondary" : "primary"}
                                        variant="contained"
                                        startIcon={<CameraAltIcon />}
                                        fullWidth
                                        sx={{ marginTop: "10px" }}
                                    >
                                        {fotoAntes ? "Imagen antes cargada" : "Agregar imagen antes"}
                                        <VisuallyHiddenInput
                                            type="file"
                                            accept="image/*"
                                            onChange={handleAntesFile}
                                        />
                                    </Button>
                                </Card>
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <Card sx={{ padding: "10px" }}>
                                    <Typography variant="h6" color="primary">
                                        <b>Después</b>
                                    </Typography>

                                    <img
                                        src={previewDespues || imagenotfound}
                                        alt="Después"
                                        width="100%"
                                    />

                                    <TextField
                                        label="Descripción después"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        sx={{ marginTop: "10px" }}
                                        value={descripcionDespues}
                                        onChange={(e) => setDescripcionDespues(e.target.value)}
                                    />

                                    <Button
                                        component="label"
                                        tabIndex={-1}
                                        size="small"
                                        color={fotoDespues ? "secondary" : "primary"}
                                        variant="contained"
                                        startIcon={<CameraAltIcon />}
                                        fullWidth
                                        sx={{ marginTop: "10px" }}
                                    >
                                        {fotoDespues ? "Imagen después cargada" : "Agregar imagen después"}
                                        <VisuallyHiddenInput
                                            type="file"
                                            accept="image/*"
                                            onChange={handleDespuesFile}
                                        />
                                    </Button>
                                </Card>
                            </Grid>

                            <Grid size={{ xs: 12, md: 12 }}>
                                <Button
                                    variant="contained"
                                    color="success"
                                    fullWidth
                                    onClick={guardarComparativa}
                                    disabled={guardandoComparativa}
                                >
                                    {comparativaEditId ? "Guardar cambios" : "Guardar registro"}
                                </Button>
                            </Grid>

                            {comparativaEditId && (
                                <Grid size={{ xs: 12, md: 12 }}>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        fullWidth
                                        onClick={limpiarFormComparativa}
                                    >
                                        Cancelar edición
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    </Card>

                    <Card sx={{ padding: "20px 10px", margin: "10px 0" }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 12 }}>
                                <center>
                                    <Typography variant="h5" color="primary">
                                        <b>Registros guardados</b>
                                    </Typography>
                                </center>
                            </Grid>

                            {comparativas.length === 0 && (
                                <Grid size={{ xs: 12, md: 12 }}>
                                    <Typography color="text.secondary">
                                        No hay registros antes/después.
                                    </Typography>
                                </Grid>
                            )}

                            {comparativas.map((item) => (
                                <Grid size={{ xs: 12, md: 12 }} key={item.id}>
                                    <Card sx={{ padding: "15px" }}>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography variant="h6" color="primary">
                                                    <b>Antes</b>
                                                </Typography>
                                                <img
                                                    src={getSrc(item.ruta_antes)}
                                                    alt="Antes"
                                                    width="100%"
                                                />
                                                <Typography variant="body1">
                                                    {item.descripcion_antes || "Sin descripción"}
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography variant="h6" color="primary">
                                                    <b>Después</b>
                                                </Typography>
                                                <img
                                                    src={getSrc(item.ruta_despues)}
                                                    alt="Después"
                                                    width="100%"
                                                />
                                                <Typography variant="body1">
                                                    {item.descripcion_despues || "Sin descripción"}
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 12 }}>
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    fullWidth
                                                    onClick={() => editarComparativa(item)}
                                                >
                                                    Editar registro
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Card>
                </>
            )}
        </>
    );
}

export default ImagenesOrden;